import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-light tracking-wide text-white transition-colors hover:text-[#FFD700] py-1 block group">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 bg-[#FFD700] transition-all duration-300 ease-out group-hover:left-0 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-full bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6 text-center">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-white hover:text-[#FFD700] transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="navigation" className="sticky top-0 z-50 w-full transition-all duration-300 border-b backdrop-blur-md border-neutral-800 shadow-xl !text-[hsl(0,0%,0%)] !bg-[hsl(0,0%,0%)]">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between !text-[hsl(0,0%,0%)]">
        {}
        <div className="flex-shrink-0 w-[200px]">
          <Link to="/" className="block">
            <img src="https://findahomewithchris.com/wp-content/uploads/2026/02/image_qai8jlym.png" alt="Find a home with Chris" className="object-contain !w-[279px] !h-[69px]" />
          </Link>
        </div>

        <img src="https://findahomewithchris.com/wp-content/uploads/2026/02/logo-1.jpg" alt="Chris" className="object-contain hidden !w-[80px] !h-[200px] lg:block" />

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="17">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0 gap-3 justify-end">
          <Button asChild className="bg-[#FFD700] text-black hover:bg-[#E6C200] font-bold px-6 shadow-sm border-none">
            <Link to="/ReachOutPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 py-2 has-[>svg]:px-3 text-black font-bold px-6 shadow-sm border-none !bg-[hsl(50,98%,64%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[#E6C200]">
              Book a Call
            </Link>
          </Button>
          <Button asChild className="bg-[#FFD700] text-black hover:bg-[#E6C200] font-bold px-6 shadow-sm border-none">
            <Link to="https://homesmart.com/real-estate-office/new-york/montgomery/75-homes-and-estates" newTab className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 py-2 has-[>svg]:px-3 text-black font-bold px-6 shadow-sm border-none !bg-[hsl(50,98%,64%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[#E6C200]">
              Homesmart
            </Link>
          </Button>
          <Button asChild className="bg-[#FFD700] text-black hover:bg-[#E6C200] font-bold px-6 shadow-sm border-none">
            <Link to="https://www.facebook.com/profile.php?id=61555645562427" newTab className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 py-2 has-[>svg]:px-3 text-black font-bold px-6 shadow-sm border-none !bg-[hsl(50,98%,64%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[#E6C200]">
              Facebook
            </Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center gap-2">
          <Button asChild size="sm" className="bg-[#FFD700] text-black hover:bg-[#E6C200] text-xs font-bold px-3 h-8">
            <Link to="/ReachOutPage">
              Book
            </Link>
          </Button>
          <Button asChild size="sm" className="bg-[#FFD700] text-black hover:bg-[#E6C200] text-xs font-bold px-3 h-8">
            <Link to="https://homesmart.com/real-estate-office/new-york/montgomery/75-homes-and-estates" newTab>
              Homesmart
            </Link>
          </Button>
          <Button asChild size="sm" className="bg-[#FFD700] text-black hover:bg-[#E6C200] text-xs font-bold px-3 h-8">
            <Link to="https://www.facebook.com/profile.php?id=61555645562427" newTab>
              Facebook
            </Link>
          </Button>
          
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:bg-neutral-900" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-black border-b border-neutral-800 shadow-2xl animate-in slide-in-from-top-5 duration-200">
          <div className="flex flex-col py-4">
            <MenuProvider menu_id="17">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            <div className="p-6 pt-0 flex flex-col items-center gap-3">
              <Button asChild className="w-full max-w-xs bg-[#FFD700] text-black hover:bg-[#E6C200] font-bold">
                <Link to="/ReachOutPage" onClick={() => setIsMobileMenuOpen(false)}>
                  Book a Call
                </Link>
              </Button>
              <Button asChild className="w-full max-w-xs bg-[#FFD700] text-black hover:bg-[#E6C200] font-bold">
                <Link to="https://homesmart.com/real-estate-office/new-york/montgomery/75-homes-and-estates" newTab onClick={() => setIsMobileMenuOpen(false)}>
                  Homesmart
                </Link>
              </Button>
              <Button asChild className="w-full max-w-xs bg-[#FFD700] text-black hover:bg-[#E6C200] font-bold">
                <Link to="https://www.youtube.com/@findahomewithchris" newTab onClick={() => setIsMobileMenuOpen(false)}>
                  YouTube Channel
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}